import { UserGroupMemberLookupStatus } from '@navio/nsdk';
import { invalidateAfterUserGroupMemberMutation, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiDialog, NoddiStepFlow, noddiToast } from 'noddi-ui-web';
import { useState } from 'react';
import { AddMemberNumberStep } from './AddMemberNumberStep';
import { AddMemberPersonStep } from './AddMemberPersonStep';
import { AddMemberSettingsStep } from './AddMemberSettingsStep';
import {
  type AddMemberDraft,
  type AddMemberLookup,
  type AddMemberSettingsValues,
  type AddMemberStepKey,
  DEFAULT_ADD_MEMBER_SETTINGS,
  EMPTY_ADD_MEMBER_DRAFT,
  getAddMemberSteps
} from './addMemberWizardSteps';
import { NotificationGroupOverviewTranslations } from './notificationTranslations';

type Props = {
  userGroupId: number;
  /** Brand whose customer app the "you were added" SMS links to. Without it the SMS has no link. */
  brandId?: number;
  translations: NotificationGroupOverviewTranslations;
  /** See `CarContactsOverview` — only the customer app needs its own success copy. */
  showMutationToasts: boolean;
  /** Called once the member exists, so the host can show the tab the new row landed in. */
  onMemberAdded?: () => void;
};

/**
 * Adding a member is a lookup and then a decision, so it is a wizard rather than one form: the
 * number is checked, the person confirmed or created, and their settings chosen. An existing account is added
 * as-is; an unknown number gets an account created from the name typed here. Nobody has to accept
 * anything — the admin vouches for the number, and the person verifies it themselves the first
 * time they log in.
 */
export const AddOrganizationMemberDialog = ({
  userGroupId,
  brandId,
  translations: t,
  showMutationToasts,
  onMemberAdded
}: Props) => {
  const [open, setOpen] = useState(false);
  const [currentStepKey, setCurrentStepKey] = useState<AddMemberStepKey>('number');
  const [completedStepKeys, setCompletedStepKeys] = useState<AddMemberStepKey[]>([]);
  const [lookup, setLookup] = useState<AddMemberLookup>();
  const [draft, setDraft] = useState<AddMemberDraft>(EMPTY_ADD_MEMBER_DRAFT);
  const [settings, setSettings] = useState<AddMemberSettingsValues>(DEFAULT_ADD_MEMBER_SETTINGS);

  const {
    mutateAsync: addMember,
    error: addError,
    reset: resetAddError
  } = noddiAsync.usePost({
    type: URLKeys.postUserGroupMemberAddByPhone,
    queryConfig: {
      meta: { suppressSuccessToast: showMutationToasts },
      onSuccess: () => invalidateAfterUserGroupMemberMutation({ userGroupId })
    }
  });

  const needsName = lookup?.status === UserGroupMemberLookupStatus.NOT_FOUND;
  const typedName = `${draft.firstName} ${draft.lastName}`.trim();

  const close = () => {
    setOpen(false);
    setCurrentStepKey('number');
    setCompletedStepKeys([]);
    setLookup(undefined);
    setDraft(EMPTY_ADD_MEMBER_DRAFT);
    setSettings(DEFAULT_ADD_MEMBER_SETTINGS);
    resetAddError();
  };

  const advanceTo = (key: AddMemberStepKey) => {
    setCompletedStepKeys((keys) => (keys.includes(currentStepKey) ? keys : [...keys, currentStepKey]));
    setCurrentStepKey(key);
  };

  const createMember = async (settings: AddMemberSettingsValues) => {
    if (!lookup) {
      return;
    }
    resetAddError();
    try {
      const { data } = await addMember({
        userGroupId,
        phoneNumber: lookup.phoneNumber,
        ...(needsName ? { firstName: draft.firstName, lastName: draft.lastName, email: draft.email || null } : {}),
        isAdmin: settings.isAdmin,
        sendSms: settings.sendSms,
        brandId: brandId ?? null
      });
      if (showMutationToasts) {
        noddiToast.success(t.memberAdded(`${data.user.firstName} ${data.user.lastName}`.trim()));
      }
      close();
      onMemberAdded?.();
    } catch {
      // Only keeps the wizard open: the customer app's mutation cache does not toast errors,
      // so the rejection is rendered from `addError` on the step instead.
      return;
    }
  };

  return (
    <>
      <NoddiButton startIcon='Plus' onClick={() => setOpen(true)}>
        {t.addMember}
      </NoddiButton>
      <NoddiDialog
        open={open}
        onClose={close}
        title={t.addMember}
        className='max-h-[85vh] min-h-96'
        contentClassName='flex min-h-0 flex-1 flex-col gap-4 overflow-hidden'
      >
        <NoddiStepFlow
          steps={getAddMemberSteps(t)}
          currentStepKey={currentStepKey}
          completedStepKeys={completedStepKeys}
          onStepChange={(key) => setCurrentStepKey(key as AddMemberStepKey)}
        />
        {/* Keyed so each step fades in, and so a step's form starts from the draft it was given. */}
        <div
          key={currentStepKey}
          className='flex min-h-0 flex-1 flex-col gap-4 motion-safe:animate-in motion-safe:duration-300 motion-safe:fade-in'
        >
          {currentStepKey === 'number' && (
            <AddMemberNumberStep
              userGroupId={userGroupId}
              phoneNumber={lookup?.phoneNumber ?? ''}
              translations={t}
              onLookedUp={(result) => {
                // Another number is another person, so nothing decided about the last one survives it.
                if (result.phoneNumber !== lookup?.phoneNumber) {
                  setDraft(EMPTY_ADD_MEMBER_DRAFT);
                  setSettings(DEFAULT_ADD_MEMBER_SETTINGS);
                  setCompletedStepKeys([]);
                }
                setLookup(result);
                advanceTo('person');
              }}
              onCancel={close}
            />
          )}
          {currentStepKey === 'person' && (
            <AddMemberPersonStep
              needsName={needsName}
              displayName={lookup?.displayName ?? ''}
              phoneNumber={lookup?.phoneNumber ?? ''}
              draft={draft}
              translations={t}
              onBack={(next) => {
                setDraft(next);
                setCurrentStepKey('number');
              }}
              onSubmit={(next) => {
                setDraft(next);
                advanceTo('settings');
              }}
            />
          )}
          {currentStepKey === 'settings' && (
            <AddMemberSettingsStep
              name={needsName ? typedName : (lookup?.displayName ?? '')}
              phoneNumber={lookup?.phoneNumber ?? ''}
              email={needsName ? draft.email : undefined}
              values={settings}
              error={addError}
              translations={t}
              onBack={(next) => {
                setSettings(next);
                setCurrentStepKey('person');
              }}
              onSubmit={createMember}
            />
          )}
        </div>
      </NoddiDialog>
    </>
  );
};
